/**
 * Consent for analytics and crash reports (plan docs 01 and 17).
 *
 * Both are off until the learner says yes, and the question is asked once,
 * after the first finished lesson — never on first launch, and never of a
 * minor, who is not asked and not tracked. The answer is kept on the device,
 * per device, because consent given on a phone is not consent given on a
 * laptop.
 */

export type Consent = 'unknown' | 'granted' | 'denied';

export interface ConsentState {
  analytics: Consent;
  errorReports: Consent;
  /** When the learner last answered, or null if never asked. */
  decidedAt: string | null;
}

export const DEFAULT_CONSENT: ConsentState = {
  analytics: 'unknown',
  errorReports: 'unknown',
  decidedAt: null,
};

const KEY = 'audigoal.consent';
const listeners = new Set<() => void>();

function isConsent(value: unknown): value is Consent {
  return value === 'unknown' || value === 'granted' || value === 'denied';
}

export function readConsent(): ConsentState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return { ...DEFAULT_CONSENT };
    const parsed = JSON.parse(raw) as Partial<Record<keyof ConsentState, unknown>>;
    return {
      analytics: isConsent(parsed.analytics) ? parsed.analytics : 'unknown',
      errorReports: isConsent(parsed.errorReports) ? parsed.errorReports : 'unknown',
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : null,
    };
  } catch {
    return { ...DEFAULT_CONSENT };
  }
}

export function writeConsent(state: ConsentState): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // Private mode: the answer holds for this visit and is asked again later.
  }
  for (const listener of listeners) listener();
}

/** Subscribes to the answer changing, from the prompt or from Settings. */
export function onConsentChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The prompt's answer: one yes or no covers both. */
export function decideConsent(granted: boolean, now = new Date()): ConsentState {
  const verdict: Consent = granted ? 'granted' : 'denied';
  const next: ConsentState = {
    analytics: verdict,
    errorReports: verdict,
    decidedAt: now.toISOString(),
  };
  writeConsent(next);
  return next;
}

/** Settings' finer control: either switch on its own. */
export function setConsent(
  patch: Partial<Pick<ConsentState, 'analytics' | 'errorReports'>>,
  now = new Date(),
): ConsentState {
  const next: ConsentState = { ...readConsent(), ...patch, decidedAt: now.toISOString() };
  writeConsent(next);
  return next;
}

/**
 * Whether to show the prompt. Pure.
 *
 * Not before a lesson has been finished, not to a minor, not twice, and not
 * when nothing is configured to receive the data — asking permission for a
 * thing that cannot happen is theatre.
 */
export function shouldAskConsent(input: {
  state: ConsentState;
  minor: boolean;
  lessonsCompleted: number;
  configured: boolean;
}): boolean {
  if (!input.configured || input.minor) return false;
  if (input.lessonsCompleted < 1) return false;
  return input.state.analytics === 'unknown' && input.state.errorReports === 'unknown';
}
